import { EVERYWHERE } from '@web-platform/shared-universal-constants/src/places';
import { camelCaseToKebabCaseMap } from '@web-platform/shared-universal-constants/src/queryParamMapper';
import {
  LAYOVER_AIRPORTS,
  LAYOVER_DURATION,
} from '@web-platform/shared-universal-constants/src/queryParams';
import { MINUTES_PER_HOUR } from '@web-platform/shared-universal-constants/src/timeDurations';
import { MULTI_DESTINATION, RETURN } from '@web-platform/shared-universal-constants/src/tripTypes';
import {
  booleanKeyValuesParamParser,
  integerRangesParamParser,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/queryParams/paramParsers';

import { getInboundDate, getOutboundDate } from './userSearchUtils';

import type {
  PartialSearchParams,
  SearchLegPlace,
  SearchLeg,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { LayoversFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/layovers';

const DAY = 'day';
const MONTH = 'month';
type StringsType = {
  ktxtURLFlights: string;
  ktxtURLFlightsFrom: string;
  ktxtURLTransport: string;
};

const getDateType = (date: string) => {
  if (date && date.length === 10) {
    return DAY;
  }

  if (date && date.length === 7) {
    return MONTH;
  }

  return null;
};

const formatDate = (date: string): string => {
  const type = getDateType(date);

  if (type) {
    return date.replace(/-/g, '').substring(2);
  }

  throw new Error(`invalid date format: ${date}`);
};

const hasDestination = (destination: SearchLegPlace | void) =>
  destination && destination.id && destination.id !== EVERYWHERE;

const isMultiDestination = (userSearch: PartialSearchParams): boolean =>
  userSearch.tripType === MULTI_DESTINATION;

const isReturn = (userSearch: PartialSearchParams): boolean => userSearch.tripType === RETURN;

const buildPlacePath = (userSearch: PartialSearchParams): string => {
  const firstLeg = userSearch.legs?.[0];
  const origin = firstLeg?.origin;

  if (!origin || !origin.id) {
    return '';
  }

  let path = `/${origin.id.toLowerCase()}`;

  const destination = firstLeg?.destination;

  if (hasDestination(destination)) {
    // FIXME: SHIBA-809 Bad typecasting - do not copy this pattern
    path += `/${(destination as SearchLegPlace).id.toLowerCase()}`;
  }

  return path;
};

const buildDatePath = (userSearch: PartialSearchParams): string => {
  let url = '';

  const inboundDate = getInboundDate(userSearch);
  const outboundDate = getOutboundDate(userSearch);

  if (outboundDate && getDateType(outboundDate) === DAY) {
    url += `/${formatDate(outboundDate)}`;

    if (isReturn(userSearch) && inboundDate && getDateType(inboundDate) === DAY) {
      url += `/${formatDate(inboundDate)}`;
    }
  }

  return url;
};

const buildUrlPathPrefix = (strings: StringsType, userSearch: PartialSearchParams): string => {
  if (isMultiDestination(userSearch)) {
    return `/${strings.ktxtURLTransport}/d`;
  }

  const firstLegDestination = userSearch.legs?.[0]?.destination;

  if (hasDestination(firstLegDestination)) {
    return `/${strings.ktxtURLTransport}/${strings.ktxtURLFlights}`;
  }

  return `/${strings.ktxtURLTransport}/${strings.ktxtURLFlightsFrom}`;
};

const buildUrlPath = (userSearch: PartialSearchParams): string => {
  if (isMultiDestination(userSearch)) {
    // @ts-expect-error TS2339: Property 'multiCityLegs' does not exist on type 'SearchParams'
    // Properties below are exclusive to the GC state and absent in the regular banana state.
    // No issues arise as this code only runs in the GC context where multicity is unsupported by Banana Search Controls, this setup is temporary.
    // TODO: [SHIBA-2957]
    if (!userSearch.multiCityLegs) {
      return '';
    }

    let path = '';
    // @ts-expect-error TS2339: Property 'multiCityLegs' does not exist on type 'SearchParams'
    // TODO: [SHIBA-2957]
    userSearch.multiCityLegs.forEach((leg: SearchLeg) => {
      path += `/${leg.origin.id.toLowerCase()}/${leg.date}/${leg.destination.id.toLowerCase()}`;
    });
    return path;
  }

  return `${buildPlacePath(userSearch)}${buildDatePath(userSearch)}/`;
};

const buildSearchParams = (
  userSearch: PartialSearchParams,
  extraSearchParams?: URLSearchParams,
  layovers?: LayoversFilterState,
): string => {
  const searchParams = new URLSearchParams();

  searchParams.set('adultsv2', (userSearch.adults || 1).toString());
  searchParams.set('cabinclass', (userSearch.cabinClass || 'economy').toLowerCase());
  searchParams.set('childrenv2', (userSearch.child_ages || []).join('|'));
  searchParams.set('ref', 'home');

  if (!isMultiDestination(userSearch)) {
    searchParams.set('rtn', isReturn(userSearch) ? '1' : '0');
    searchParams.set('preferdirects', (!!userSearch.prefer_directs).toString());
    searchParams.set('outboundaltsenabled', (!!userSearch.alternativeOrigins).toString());
    searchParams.set('inboundaltsenabled', (!!userSearch.alternativeDestinations).toString());
    const fareAttributes = userSearch.fareAttributes || [];
    if (fareAttributes.length > 0) {
      searchParams.set(
        'fare-attributes',
        fareAttributes
          .map((camelCaseKey) => camelCaseToKebabCaseMap[camelCaseKey] ?? camelCaseKey)
          .join(','),
      );
    }
    const outboundDate = getOutboundDate(userSearch);

    if (outboundDate && getDateType(outboundDate) === MONTH) {
      searchParams.set('oym', formatDate(outboundDate));
    }

    const inboundDate = getInboundDate(userSearch);

    if (inboundDate && getDateType(inboundDate) === MONTH && isReturn(userSearch)) {
      searchParams.set('iym', formatDate(inboundDate));
    }
  }

  if (layovers) {
    const { duration, excludedAirports } = layovers;

    if (duration.min !== null && duration.max !== null) {
      const serialized = integerRangesParamParser.serialize([
        {
          start: Math.round(duration.min * MINUTES_PER_HOUR),
          end: Math.round(duration.max * MINUTES_PER_HOUR),
        },
      ]);
      if (serialized) {
        searchParams.set(LAYOVER_DURATION, serialized);
      }
    }

    if (excludedAirports.length > 0) {
      const serialized = booleanKeyValuesParamParser.serialize(
        excludedAirports.map((code) => ({ key: code, value: false })),
      );
      if (serialized) {
        searchParams.set(LAYOVER_AIRPORTS, serialized);
      }
    }
  }

  (extraSearchParams || new URLSearchParams()).forEach((valueOfParam, keyOfParam) => {
    if (!searchParams.has(keyOfParam)) {
      searchParams.set(keyOfParam, valueOfParam);
    }
  });

  return `?${searchParams.toString()}`;
};

const buildUrl = (
  strings: StringsType,
  userSearch: PartialSearchParams,
  extraSearchParams?: URLSearchParams,
  layovers?: LayoversFilterState,
): string => {
  if (!userSearch.tripType) {
    throw new Error('Invalid tripType');
  }

  return `${buildUrlPathPrefix(strings, userSearch)}${buildUrlPath(userSearch)}${buildSearchParams(
    userSearch,
    extraSearchParams,
    layovers,
  )}`;
};

export {
  buildUrl,
  buildUrlPath,
  buildPlacePath,
  buildUrlPathPrefix,
  buildSearchParams,
  isMultiDestination,
};
